import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ColorNeutralDarkGrey, ColorNeutralWhite, Spacing16, Spacing24 } from 'autogen/design-tokens/tokens';
import { ByAuthor } from 'autogen/translation-keys/trans-website-articleblock';
import type { EntryFields } from 'contentful';
import useSegmentation from 'hooks/user/useSegmentation';
import { removeLinkTextDecoration } from 'modules/helpers/mixins';
import type { TypeAuthorDetailed } from 'types/autogen/contentful';
import { breakpoint } from 'variables';
import Image from '../../Image/Image';
import WithAction from '../../shared/WithAction';

// #region Styles
const Wrapper = styled.div`
    display: flex; 
    gap: ${Spacing16};
    align-items: center;
    background-color: ${ColorNeutralWhite};
    padding: ${Spacing24};
    
    ${breakpoint.up('1025px')} {
        flex-direction: column;        
    }
`;

const StyledImage = styled(Image)`
    border-radius: 100%;
    aspect-ratio: 1;
    object-fit: cover;
    max-width: 80px;
    max-height: 80px;
    width: 100%;
    height: 100%;

    ${breakpoint.up('1025px')} {
        max-width: 120px;
        max-height: 120px;
    }
`;

const InfoContainer = styled.div`
    color: ${ColorNeutralDarkGrey};
    
    ${breakpoint.up('1025px')} {
        text-align: center;
    }
`;

const StyledWithAction = styled(WithAction)`
    ${removeLinkTextDecoration}
`;

const Address = styled.address`
    margin: 0;
`;
// #endregion Styles

function formatDate(dateString: EntryFields.Date, locale: string) {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'long', year: 'numeric' };
    const formattedDate = new Intl.DateTimeFormat(locale, options).format(date);

    return formattedDate;
}

type AuthorCardProps = TypeAuthorDetailed & {
    publishDate: EntryFields.Date;
};

export default function AuthorCard(props: AuthorCardProps) {
    const { fields, publishDate } = props;
    const { firstName, lastName, link, portrait } = fields;
    const { t } = useTranslation();
    const { culture } = useSegmentation();
    const formattedPublishDate = formatDate(publishDate, culture);

    return (
        <StyledWithAction link={link} rel="author">
            <Wrapper>
                {portrait && <StyledImage {...portrait} sizes="(min-width: 1025px) 120px, 80px" />}
                <InfoContainer>
                    {(firstName || lastName) && (
                        <div>
                            <Address>
                                {t(ByAuthor)} {firstName} {lastName}
                            </Address>
                            <time dateTime={publishDate}>{formattedPublishDate}</time>
                        </div>
                    )}
                </InfoContainer>
            </Wrapper>
        </StyledWithAction>
    );
}
